import { useCallback, useState } from 'react'
import { lumi } from '@/lib/lumi'

/**
 * Type de qualification principale d'un candidat dans la CVthèque
 * "Disponibles Immédiatement !".
 */
export type CandidateQualification =
  | 'BNSSA'
  | 'BPJEPS AAN'
  | 'BEESAN'
  | 'STAPS'
  | 'MNS'

// MNS est volontairement retiré des choix de qualification CVthèque
// (les profils MNS existants en base restent lisibles grâce au type ci-dessus,
// mais MNS n'est plus proposé ni comme choix de saisie ni comme filtre).
export const CANDIDATE_QUALIFICATIONS: CandidateQualification[] = [
  'BNSSA',
  'BPJEPS AAN',
  'BEESAN',
  'STAPS',
]

export type CandidateMobility = 'non' | 'locale' | 'departementale' | 'regionale' | 'france'

/**
 * Spécialité du candidat (profils "métier de bassin" : MNS / BPJEPS AAN / BEESAN / STAPS).
 * Champ privé : grisé/flouté tant que le recruteur n'est pas connecté.
 * Non applicable aux BNSSA (uniquement de la surveillance).
 */
export type CandidateSpecialty =
  | 'Aquafitness'
  | 'Enseignement scolaire'
  | 'Ecole de natation'
  | 'Natation competition'
  | 'Polyvalent'
  | 'Autre'

export const CANDIDATE_SPECIALTIES: CandidateSpecialty[] = [
  'Aquafitness',
  'Enseignement scolaire',
  'Ecole de natation',
  'Natation competition',
  'Polyvalent',
  'Autre',
]

/** Libellés d'affichage (avec accents) des spécialités. */
export const SPECIALTY_LABELS: Record<CandidateSpecialty, string> = {
  Aquafitness: 'Aquafitness',
  'Enseignement scolaire': 'Enseignement scolaire',
  'Ecole de natation': 'École de natation',
  'Natation competition': 'Natation compétition',
  Polyvalent: 'Polyvalent',
  Autre: 'Autre',
}

/**
 * Milieu de travail privilégié par le candidat.
 * Champ privé : grisé/flouté tant que le recruteur n'est pas connecté.
 */
export type CandidateWorkEnvironment = 'Mer' | 'Piscine' | 'Autre milieu naturel' | 'Tout'

export const WORK_ENVIRONMENTS: CandidateWorkEnvironment[] = [
  'Mer',
  'Piscine',
  'Autre milieu naturel',
  'Tout',
]

/**
 * Niveau d'expérience du candidat (tranches).
 * Champ privé : grisé/flouté tant que le recruteur n'est pas connecté.
 */
export type CandidateExperience =
  | '< 1 an'
  | '1-2 ans'
  | '3-5 ans'
  | '5-10 ans'
  | '10-20 ans'
  | '+20 ans'

export const EXPERIENCE_LEVELS: CandidateExperience[] = [
  '< 1 an',
  '1-2 ans',
  '3-5 ans',
  '5-10 ans',
  '10-20 ans',
  '+20 ans',
]

export const MOBILITY_LABELS: Record<CandidateMobility, string> = {
  non: 'Non mobile',
  locale: 'Mobilité locale',
  departementale: 'Mobilité départementale',
  regionale: 'Mobilité régionale',
  france: 'Partout en France',
}

/**
 * Liste des départements français (métropole + DROM) au format "NN — Nom".
 * Utilisée pour la mobilité départementale (champ privé, visible recruteurs uniquement).
 */
export const FRENCH_DEPARTMENTS: string[] = [
  '01 — Ain', '02 — Aisne', '03 — Allier', '04 — Alpes-de-Haute-Provence',
  '05 — Hautes-Alpes', '06 — Alpes-Maritimes', '07 — Ardèche', '08 — Ardennes',
  '09 — Ariège', '10 — Aube', '11 — Aude', '12 — Aveyron',
  '13 — Bouches-du-Rhône', '14 — Calvados', '15 — Cantal', '16 — Charente',
  '17 — Charente-Maritime', '18 — Cher', '19 — Corrèze', '2A — Corse-du-Sud',
  '2B — Haute-Corse', '21 — Côte-d\'Or', '22 — Côtes-d\'Armor', '23 — Creuse',
  '24 — Dordogne', '25 — Doubs', '26 — Drôme', '27 — Eure',
  '28 — Eure-et-Loir', '29 — Finistère', '30 — Gard', '31 — Haute-Garonne',
  '32 — Gers', '33 — Gironde', '34 — Hérault', '35 — Ille-et-Vilaine',
  '36 — Indre', '37 — Indre-et-Loire', '38 — Isère', '39 — Jura',
  '40 — Landes', '41 — Loir-et-Cher', '42 — Loire', '43 — Haute-Loire',
  '44 — Loire-Atlantique', '45 — Loiret', '46 — Lot', '47 — Lot-et-Garonne',
  '48 — Lozère', '49 — Maine-et-Loire', '50 — Manche', '51 — Marne',
  '52 — Haute-Marne', '53 — Mayenne', '54 — Meurthe-et-Moselle', '55 — Meuse',
  '56 — Morbihan', '57 — Moselle', '58 — Nièvre', '59 — Nord',
  '60 — Oise', '61 — Orne', '62 — Pas-de-Calais', '63 — Puy-de-Dôme',
  '64 — Pyrénées-Atlantiques', '65 — Hautes-Pyrénées', '66 — Pyrénées-Orientales',
  '67 — Bas-Rhin', '68 — Haut-Rhin', '69 — Rhône', '70 — Haute-Saône',
  '71 — Saône-et-Loire', '72 — Sarthe', '73 — Savoie', '74 — Haute-Savoie',
  '75 — Paris', '76 — Seine-Maritime', '77 — Seine-et-Marne', '78 — Yvelines',
  '79 — Deux-Sèvres', '80 — Somme', '81 — Tarn', '82 — Tarn-et-Garonne',
  '83 — Var', '84 — Vaucluse', '85 — Vendée', '86 — Vienne',
  '87 — Haute-Vienne', '88 — Vosges', '89 — Yonne', '90 — Territoire de Belfort',
  '91 — Essonne', '92 — Hauts-de-Seine', '93 — Seine-Saint-Denis',
  '94 — Val-de-Marne', '95 — Val-d\'Oise',
  '971 — Guadeloupe', '972 — Martinique', '973 — Guyane',
  '974 — La Réunion', '976 — Mayotte',
]

/** Rayons proposés (en km) lorsque le candidat coche "Mobilité locale". */
export type MobilityRadiusKm = 10 | 20 | 30 | 50 | 100
export const MOBILITY_RADIUS_OPTIONS: MobilityRadiusKm[] = [10, 20, 30, 50, 100]

export type CandidateHousingNeed = 'non' | 'oui' | 'oui_si_eloigne'

export const HOUSING_LABELS: Record<CandidateHousingNeed, string> = {
  non: 'Pas besoin',
  oui: 'Oui',
  oui_si_eloigne: 'Oui si mission éloignée',
}

/**
 * Mode de disponibilité :
 * - 'immediate' : "Disponible Immédiatement !" (fenêtre glissante 48h, existant).
 * - 'period'    : "Disponible du JJ/MM au JJ/MM" (créneau daté planifié à l'avance).
 */
export type AvailabilityMode = 'immediate' | 'period'

/**
 * Formate une plage de dates de disponibilité "période" sous la forme
 * lisible "du 12/07 au 31/08" (jour/mois). Retourne '' si invalide.
 */
export function formatPeriodRange(periodStart?: string, periodEnd?: string): string {
  if (!periodStart || !periodEnd) return ''
  const s = new Date(periodStart)
  const e = new Date(periodEnd)
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return ''
  const fmt = (d: Date) =>
    `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${d.getFullYear()}`
  return `du ${fmt(s)} au ${fmt(e)}`
}

/**
 * Renvoie l'état d'une disponibilité "période" par rapport à aujourd'hui :
 * - 'upcoming' : commence plus tard
 * - 'active'   : en cours (aujourd'hui est dans la plage)
 * - 'ended'    : terminée
 */
export function getPeriodStatus(
  periodStart?: string,
  periodEnd?: string
): 'upcoming' | 'active' | 'ended' | 'unknown' {
  if (!periodStart || !periodEnd) return 'unknown'
  const now = Date.now()
  const s = new Date(periodStart).getTime()
  const e = new Date(periodEnd).getTime()
  if (Number.isNaN(s) || Number.isNaN(e)) return 'unknown'
  if (now < s) return 'upcoming'
  if (now > e) return 'ended'
  return 'active'
}

export const FRENCH_REGIONS: string[] = [
  'Auvergne-Rhône-Alpes',
  'Bourgogne-Franche-Comté',
  'Bretagne',
  'Centre-Val de Loire',
  'Corse',
  'Grand Est',
  'Hauts-de-France',
  'Île-de-France',
  'Normandie',
  'Nouvelle-Aquitaine',
  'Occitanie',
  'Pays de la Loire',
  "Provence-Alpes-Côte d'Azur",
  'Guadeloupe',
  'Martinique',
  'Guyane',
  'La Réunion',
  'Mayotte',
]

/** Durée de validité d'une publication "Disponible maintenant !". */
export const AVAILABILITY_DURATION_HOURS = 48
export const AVAILABILITY_DURATION_MS = AVAILABILITY_DURATION_HOURS * 60 * 60 * 1000

/**
 * Formate l'identifiant public d'un candidat sous la forme "<QUALIFICATION>-<publicId>",
 * en remplaçant les espaces de la qualification par des tirets.
 * Exemples : "BNSSA-A7K9X2", "BPJEPS-AAN-A7K9X2".
 */
export function formatProfileLabel(
  qualification?: string | null,
  publicId?: string | null
): string {
  const q = (qualification ?? '').trim().replace(/\s+/g, '-')
  const id = publicId ?? '——'
  return `${q}-${id}`
}

/** Génère un identifiant public court (6 caractères alphanumériques majuscules). */
export function generatePublicId(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789' // sans I, O, 0, 1
  let id = ''
  for (let i = 0; i < 6; i++) {
    id += alphabet[Math.floor(Math.random() * alphabet.length)]
  }
  return id
}

/**
 * Formate le temps écoulé depuis la mise en ligne sous la forme :
 *   "En ligne" (< 1h) / "En ligne il y a 3 h" / "En ligne il y a 1 j 4 h".
 */
export function formatOnline(availableSince?: string): string {
  if (!availableSince) return 'En ligne'
  const start = new Date(availableSince).getTime()
  if (Number.isNaN(start)) return 'En ligne'
  const diff = Date.now() - start
  if (diff < 0) return 'En ligne'
  const totalMinutes = Math.floor(diff / 60000)
  if (totalMinutes < 60) return 'En ligne'
  const hours = Math.floor(totalMinutes / 60)
  if (hours < 24) return `En ligne il y a ${hours} h`
  const days = Math.floor(hours / 24)
  const remHours = hours - days * 24
  return remHours > 0
    ? `En ligne il y a ${days} j ${remHours} h`
    : `En ligne il y a ${days} j`
}

/**
 * Formate le temps restant avant expiration sous la forme :
 *   "Encore disponible 1 j 10 h" / "Encore 5 h 32 min" / "Expiré".
 */
export function formatRemaining(availableUntil?: string): string {
  if (!availableUntil) return ''
  const end = new Date(availableUntil).getTime()
  if (Number.isNaN(end)) return ''
  const diff = end - Date.now()
  if (diff <= 0) return 'Expiré'
  const totalMinutes = Math.floor(diff / 60000)
  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes - days * 60 * 24) / 60)
  const minutes = totalMinutes - days * 60 * 24 - hours * 60
  if (days >= 1) return `Encore disponible ${days} j ${hours} h`
  if (hours >= 1) return `Encore ${hours} h ${minutes.toString().padStart(2, '0')} min`
  return `Encore ${minutes} min`
}

export interface AvailableCandidate {
  _id: string
  creator: string
  creatorEmail?: string
  /** Identifiant public court (ex. A7K9X2) — affiché en CVthèque comme <qualification>-<publicId>. */
  publicId?: string
  /** Téléphone de contact (caché en public, visible recruteurs). */
  phone?: string
  /** Email de contact (caché en public, visible recruteurs). */
  email?: string
  qualification: CandidateQualification
  city: string
  region: string
  /** Spécialité (privée, visible recruteurs uniquement). */
  specialty?: CandidateSpecialty
  /** Milieu de travail privilégié (privé, visible recruteurs uniquement). */
  workEnvironment?: CandidateWorkEnvironment
  /** Niveau d'expérience (privé, visible recruteurs uniquement). */
  experience?: CandidateExperience
  mobility: CandidateMobility
  /** Rayon en km, requis lorsque mobility === 'locale' */
  mobilityRadiusKm?: MobilityRadiusKm
  /** Région souhaitée, requis lorsque mobility === 'regionale' */
  mobilityRegion?: string
  /** Département souhaité, requis lorsque mobility === 'departementale' (champ privé). */
  mobilityDepartment?: string
  housingNeed: CandidateHousingNeed
  /** Mode de disponibilité : immédiate (48h) ou période datée. Défaut: 'immediate'. */
  availabilityMode?: AvailabilityMode
  /** Date de début de disponibilité (mode 'period', ISO). */
  periodStart?: string
  /** Date de fin de disponibilité (mode 'period', ISO). */
  periodEnd?: string
  /** Date/heure de mise en ligne du profil (ISO). */
  availableSince?: string
  /** Date/heure d'expiration (= availableSince + 48h, ISO). */
  availableUntil?: string
  isPublished?: boolean
  /** Vrai si le candidat se déclare immédiatement disponible. */
  isAvailableNow?: boolean
  /** Date d'envoi du dernier email de réactivation. */
  expirationEmailSentAt?: string
  /** URL OSS du diplôme téléversé directement depuis le form "Disponible Immédiatement"
   *  lorsque le dossier auto-entrepreneur n'est pas validé. Permet d'afficher
   *  le badge "Diplôme vérifié" en CVthèque. */
  diplomaUrl?: string
  /** [Déprécié] anciens champs gardés pour rétro-compat / lecture. */
  firstName?: string
  lastName?: string
  photoUrl?: string
  createdAt: string
  updatedAt: string
}

export interface AvailableCandidateInput {
  qualification: CandidateQualification
  city: string
  region: string
  /** Spécialité (privée, profils MNS uniquement). */
  specialty?: CandidateSpecialty | ''
  /** Milieu de travail privilégié (privé, BNSSA uniquement). */
  workEnvironment?: CandidateWorkEnvironment | ''
  /** Niveau d'expérience (privé). */
  experience?: CandidateExperience | ''
  mobility: CandidateMobility
  mobilityRadiusKm?: MobilityRadiusKm
  mobilityRegion?: string
  mobilityDepartment?: string
  housingNeed: CandidateHousingNeed
  phone: string
  email: string
  isPublished?: boolean
  isAvailableNow?: boolean
  /** Mode de disponibilité choisi (défaut 'immediate'). */
  availabilityMode?: AvailabilityMode
  /** Date de début (mode 'period', ISO ou YYYY-MM-DD). */
  periodStart?: string
  /** Date de fin (mode 'period', ISO ou YYYY-MM-DD). */
  periodEnd?: string
  creatorEmail?: string
  /** URL du diplôme téléversé (optionnel) — utilisé seulement si pas de dossier AE validé. */
  diplomaUrl?: string | null
}

/**
 * Hook CRUD pour la CVthèque "Disponibles Immédiatement !".
 * - 1 enregistrement par candidat (creator), upsert.
 * - Profil valide pendant 48h max ; ensuite isPublished doit être réactivé.
 */
export function useAvailableCandidates() {
  const [items, setItems] = useState<AvailableCandidate[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Récupère la liste publique des candidats encore valides (< 48h). */
  const fetchPublic = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const nowIso = new Date().toISOString()
      const { list } = await lumi.entities.available_candidates.list({
        filter: {
          isPublished: true,
          isAvailableNow: true,
          availableUntil: { $gte: nowIso },
        },
        sort: { updatedAt: -1 },
        limit: 200,
      })
      setItems(list as AvailableCandidate[])
    } catch (e) {
      console.error('[available_candidates] fetchPublic failed:', e)
      setError('Impossible de charger les candidats disponibles.')
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Récupère TOUS les candidats publics encore valides en une seule liste :
   * - disponibles immédiatement (fenêtre 48h non expirée),
   * - disponibles sur une période (mode 'period', date de fin non passée).
   * Les doublons éventuels (même _id) sont dédupliqués et la liste est triée
   * "disponibles maintenant" d'abord, puis périodes à venir.
   */
  const fetchAllPublic = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const nowIso = new Date().toISOString()
      const [nowRes, periodRes] = await Promise.all([
        lumi.entities.available_candidates.list({
          filter: {
            isPublished: true,
            isAvailableNow: true,
            availableUntil: { $gte: nowIso },
          },
          sort: { updatedAt: -1 },
          limit: 200,
        }),
        lumi.entities.available_candidates.list({
          filter: {
            isPublished: true,
            availabilityMode: 'period',
            periodEnd: { $gte: nowIso },
          },
          sort: { periodStart: 1 },
          limit: 200,
        }),
      ])
      const byId = new Map<string, AvailableCandidate>()
      ;(nowRes.list as AvailableCandidate[]).forEach((c) => byId.set(c._id, c))
      ;(periodRes.list as AvailableCandidate[]).forEach((c) => {
        if (!byId.has(c._id)) byId.set(c._id, c)
      })
      const merged = Array.from(byId.values()).sort((a, b) => {
        const aNow = a.isAvailableNow === true && a.availabilityMode !== 'period'
        const bNow = b.isAvailableNow === true && b.availabilityMode !== 'period'
        if (aNow !== bNow) return aNow ? -1 : 1
        // "maintenant" : plus récents en ligne d'abord ; "période" : début le plus proche.
        if (aNow && bNow) {
          return (
            new Date(b.availableSince || b.updatedAt).getTime() -
            new Date(a.availableSince || a.updatedAt).getTime()
          )
        }
        return (
          new Date(a.periodStart || a.updatedAt).getTime() -
          new Date(b.periodStart || b.updatedAt).getTime()
        )
      })
      setItems(merged)
    } catch (e) {
      console.error('[available_candidates] fetchAllPublic failed:', e)
      setError('Impossible de charger les candidats disponibles.')
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Récupère la liste publique des candidats "disponibles sur une période" :
   * mode 'period', publiés, dont la date de fin n'est pas encore passée.
   */
  const fetchPublicPeriods = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const nowIso = new Date().toISOString()
      const { list } = await lumi.entities.available_candidates.list({
        filter: {
          isPublished: true,
          availabilityMode: 'period',
          periodEnd: { $gte: nowIso },
        },
        sort: { periodStart: 1 },
        limit: 200,
      })
      setItems(list as AvailableCandidate[])
    } catch (e) {
      console.error('[available_candidates] fetchPublicPeriods failed:', e)
      setError('Impossible de charger les disponibilités à venir.')
    } finally {
      setLoading(false)
    }
  }, [])

  /** Récupère le profil disponibilité du candidat connecté (le plus récent). */
  const fetchMine = useCallback(
    async (creator: string): Promise<AvailableCandidate | null> => {
      if (!creator) return null
      try {
        const { list } = await lumi.entities.available_candidates.list({
          filter: { creator },
          sort: { updatedAt: -1 },
          limit: 1,
        })
        const arr = list as AvailableCandidate[]
        return arr.length > 0 ? arr[0] : null
      } catch (e) {
        console.error('[available_candidates] fetchMine failed:', e)
        return null
      }
    },
    []
  )

  /** Construit le bloc availableSince/availableUntil pour 48h. */
  function build48hWindow() {
    const now = new Date()
    const end = new Date(now.getTime() + AVAILABILITY_DURATION_MS)
    return { availableSince: now.toISOString(), availableUntil: end.toISOString() }
  }

  /** Crée ou met à jour le profil disponibilité du candidat. */
  const upsert = useCallback(
    async (
      data: AvailableCandidateInput,
      creator: string
    ): Promise<AvailableCandidate> => {
      setSaving(true)
      setError(null)
      try {
        const now = new Date().toISOString()
        const existing = await fetchMine(creator)

        const mode: AvailabilityMode = data.availabilityMode === 'period' ? 'period' : 'immediate'
        // BNSSA : pas de spécialité (surveillance uniquement) → on ne garde que le milieu.
        // MNS (BEESAN / BPJEPS AAN / STAPS) : pas de milieu de travail → on ne garde que la spécialité.
        const isBnssa = data.qualification === 'BNSSA'
        const payload: Record<string, unknown> = {
          qualification: data.qualification,
          city: data.city.trim(),
          region: data.region.trim(),
          specialty: isBnssa ? null : data.specialty || null,
          workEnvironment: isBnssa ? data.workEnvironment || null : null,
          experience: data.experience || null,
          mobility: data.mobility,
          housingNeed: data.housingNeed,
          phone: data.phone.trim(),
          email: data.email.trim().toLowerCase(),
          isPublished: data.isPublished !== false,
          // En mode 'period', le candidat n'est pas "disponible maintenant".
          isAvailableNow: mode === 'immediate' ? data.isAvailableNow === true : false,
          availabilityMode: mode,
          updatedAt: now,
        }

        // Mode période : on stocke les dates et on neutralise la fenêtre 48h.
        if (mode === 'period') {
          payload.periodStart = data.periodStart
            ? new Date(data.periodStart).toISOString()
            : null
          payload.periodEnd = data.periodEnd ? new Date(data.periodEnd).toISOString() : null
          payload.availableSince = null
          payload.availableUntil = null
          payload.expirationEmailSentAt = null
        } else {
          // Mode immédiat : on efface d'éventuelles dates de période.
          payload.periodStart = null
          payload.periodEnd = null
        }
        if (data.creatorEmail) payload.creatorEmail = data.creatorEmail.toLowerCase()
        if (data.diplomaUrl !== undefined) {
          payload.diplomaUrl = data.diplomaUrl || null
        }

        // Mobilité
        if (data.mobility === 'locale' && data.mobilityRadiusKm) {
          payload.mobilityRadiusKm = data.mobilityRadiusKm
          payload.mobilityRegion = null
          payload.mobilityDepartment = null
        } else if (data.mobility === 'departementale' && data.mobilityDepartment) {
          payload.mobilityDepartment = data.mobilityDepartment
          payload.mobilityRadiusKm = null
          payload.mobilityRegion = null
        } else if (data.mobility === 'regionale' && data.mobilityRegion) {
          payload.mobilityRegion = data.mobilityRegion
          payload.mobilityRadiusKm = null
          payload.mobilityDepartment = null
        } else {
          payload.mobilityRadiusKm = null
          payload.mobilityRegion = null
          payload.mobilityDepartment = null
        }

        // Fenêtre 48h : (re)déclenchée en mode immédiat si le candidat passe en
        // "disponible immédiatement" ou si son profil avait expiré / n'avait pas de date.
        if (mode === 'immediate') {
          const wasExpired = existing?.availableUntil
            ? new Date(existing.availableUntil).getTime() < Date.now()
            : true
          const wasNotPublished = !(existing?.isPublished !== false && existing?.isAvailableNow)
          if (data.isPublished !== false && data.isAvailableNow && (wasExpired || wasNotPublished || !existing?.availableSince)) {
            const { availableSince, availableUntil } = build48hWindow()
            payload.availableSince = availableSince
            payload.availableUntil = availableUntil
            payload.expirationEmailSentAt = null
          }
        }

        if (existing && existing._id) {
          // Conserve publicId existant, génère sinon
          if (!existing.publicId) payload.publicId = generatePublicId()
          const updated = await lumi.entities.available_candidates.update(
            existing._id,
            payload
          )
          console.log('[available_candidates] update OK', existing._id)
          return updated as AvailableCandidate
        }

        payload.creator = creator
        payload.publicId = generatePublicId()
        payload.createdAt = now
        if (!payload.availableSince) {
          // Profil créé en mode "non disponible" : pas de fenêtre encore.
          payload.availableSince = null
          payload.availableUntil = null
        }
        const created = await lumi.entities.available_candidates.create(payload)
        console.log('[available_candidates] create OK')
        return created as AvailableCandidate
      } catch (e) {
        console.error('[available_candidates] upsert failed:', e)
        setError("Impossible d'enregistrer votre profil.")
        throw e
      } finally {
        setSaving(false)
      }
    },
    [fetchMine]
  )

  /**
   * Réactive le profil pour 48h supplémentaires :
   * - isPublished=true, isAvailableNow=true
   * - availableSince=now, availableUntil=now+48h
   * - expirationEmailSentAt=null
   */
  const reactivate = useCallback(
    async (creator: string): Promise<AvailableCandidate | null> => {
      const existing = await fetchMine(creator)
      if (!existing?._id) return null
      const { availableSince, availableUntil } = build48hWindow()
      const payload = {
        isPublished: true,
        isAvailableNow: true,
        availableSince,
        availableUntil,
        expirationEmailSentAt: null,
        updatedAt: new Date().toISOString(),
      }
      try {
        const updated = await lumi.entities.available_candidates.update(existing._id, payload)
        console.log('[available_candidates] reactivate OK', existing._id)
        return updated as AvailableCandidate
      } catch (e) {
        console.error('[available_candidates] reactivate failed:', e)
        throw e
      }
    },
    [fetchMine]
  )

  /**
   * Marque un profil comme expiré côté DB (isPublished=false) et stocke
   * la date d'envoi de l'email de réactivation, pour ne pas spammer.
   */
  const markExpired = useCallback(
    async (id: string): Promise<void> => {
      try {
        await lumi.entities.available_candidates.update(id, {
          isPublished: false,
          expirationEmailSentAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })
        console.log('[available_candidates] markExpired OK', id)
      } catch (e) {
        console.error('[available_candidates] markExpired failed:', e)
      }
    },
    []
  )

  /** [Admin] Récupère TOUS les profils disponibilité (publiés ou non). */
  const fetchAll = useCallback(async (): Promise<AvailableCandidate[]> => {
    setLoading(true)
    setError(null)
    try {
      const { list } = await lumi.entities.available_candidates.list({
        sort: { updatedAt: -1 },
        limit: 500,
      })
      const arr = (list as AvailableCandidate[]) || []
      setItems(arr)
      return arr
    } catch (e) {
      console.error('[available_candidates] fetchAll failed:', e)
      setError('Impossible de charger les profils disponibilité.')
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  /** [Admin] Met à jour partiellement n'importe quel profil par son _id. */
  const adminUpdate = useCallback(
    async (
      id: string,
      patch: Partial<AvailableCandidateInput> & {
        isPublished?: boolean
        isAvailableNow?: boolean
        availableSince?: string | null
        availableUntil?: string | null
        publicId?: string
      }
    ): Promise<AvailableCandidate> => {
      setSaving(true)
      setError(null)
      try {
        const now = new Date().toISOString()
        const payload: Record<string, unknown> = { updatedAt: now }

        if (patch.qualification !== undefined) payload.qualification = patch.qualification
        if (patch.city !== undefined) payload.city = patch.city.trim()
        if (patch.region !== undefined) payload.region = patch.region.trim()
        if (patch.specialty !== undefined) payload.specialty = patch.specialty || null
        if (patch.workEnvironment !== undefined) payload.workEnvironment = patch.workEnvironment || null
        if (patch.experience !== undefined) payload.experience = patch.experience || null
        if (patch.housingNeed !== undefined) payload.housingNeed = patch.housingNeed
        if (patch.phone !== undefined) payload.phone = patch.phone.trim()
        if (patch.email !== undefined) payload.email = patch.email.trim().toLowerCase()
        if (patch.publicId !== undefined) payload.publicId = patch.publicId
        if (patch.isPublished !== undefined) payload.isPublished = patch.isPublished
        if (patch.isAvailableNow !== undefined) payload.isAvailableNow = patch.isAvailableNow
        if (patch.availableSince !== undefined) payload.availableSince = patch.availableSince
        if (patch.availableUntil !== undefined) payload.availableUntil = patch.availableUntil

        if (patch.mobility !== undefined) {
          payload.mobility = patch.mobility
          if (patch.mobility === 'locale') {
            payload.mobilityRadiusKm = patch.mobilityRadiusKm ?? null
            payload.mobilityRegion = null
            payload.mobilityDepartment = null
          } else if (patch.mobility === 'departementale') {
            payload.mobilityDepartment = patch.mobilityDepartment ?? null
            payload.mobilityRadiusKm = null
            payload.mobilityRegion = null
          } else if (patch.mobility === 'regionale') {
            payload.mobilityRegion = patch.mobilityRegion ?? null
            payload.mobilityRadiusKm = null
            payload.mobilityDepartment = null
          } else {
            payload.mobilityRadiusKm = null
            payload.mobilityRegion = null
            payload.mobilityDepartment = null
          }
        } else {
          if (patch.mobilityRadiusKm !== undefined) payload.mobilityRadiusKm = patch.mobilityRadiusKm
          if (patch.mobilityRegion !== undefined) payload.mobilityRegion = patch.mobilityRegion
          if (patch.mobilityDepartment !== undefined) payload.mobilityDepartment = patch.mobilityDepartment
        }

        const updated = await lumi.entities.available_candidates.update(id, payload)
        console.log('[available_candidates] adminUpdate OK', id)
        setItems((prev) =>
          prev.map((it) => (it._id === id ? ({ ...it, ...payload } as AvailableCandidate) : it))
        )
        return updated as AvailableCandidate
      } catch (e) {
        console.error('[available_candidates] adminUpdate failed:', e)
        setError("Impossible de mettre à jour ce profil.")
        throw e
      } finally {
        setSaving(false)
      }
    },
    []
  )

  /** [Admin] Réactive un profil pour 48h supplémentaires depuis la table d'admin. */
  const adminReactivate = useCallback(
    async (id: string): Promise<AvailableCandidate> => {
      const { availableSince, availableUntil } = build48hWindow()
      return adminUpdate(id, {
        isPublished: true,
        isAvailableNow: true,
        availableSince,
        availableUntil,
      })
    },
    [adminUpdate]
  )

  /** [Admin] Supprime un profil par son _id. */
  const adminDelete = useCallback(async (id: string): Promise<void> => {
    if (!id) return
    try {
      await lumi.entities.available_candidates.delete(id)
      console.log('[available_candidates] adminDelete OK', id)
      setItems((prev) => prev.filter((it) => it._id !== id))
    } catch (e) {
      console.error('[available_candidates] adminDelete failed:', e)
      throw e
    }
  }, [])

  /** Supprime le profil du candidat (retrait de la CVthèque). */
  const removeMine = useCallback(
    async (creator: string): Promise<void> => {
      if (!creator) return
      const existing = await fetchMine(creator)
      if (!existing?._id) return
      try {
        await lumi.entities.available_candidates.delete(existing._id)
        console.log('[available_candidates] delete OK', existing._id)
      } catch (e) {
        console.error('[available_candidates] delete failed:', e)
        throw e
      }
    },
    [fetchMine]
  )

  return {
    items,
    loading,
    saving,
    error,
    fetchPublic,
    fetchPublicPeriods,
    fetchAllPublic,
    fetchMine,
    upsert,
    reactivate,
    markExpired,
    removeMine,
    fetchAll,
    adminUpdate,
    adminReactivate,
    adminDelete,
  }
}
